import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from '@src/scripts/util/currency';

const WIDTH = 340;
const HEIGHT = 220;
const PADDING_TOP = 10;
const PADDING_RIGHT = 12;
const PADDING_BOTTOM = 34;
const PADDING_LEFT = 66;
const Y_TICK_COUNT = 4;
const BAR_WIDTH = 60;
const BAR_GAP = 32;
const BAR_RADIUS = 8;

// Rounds a max value up to a "nice" round number (1/2/5/10 × a power of
// ten) so the y-axis reads 25.000 / 50.000 / 75.000 / 100.000 rather than
// whatever odd figure the data happens to produce — mirrors the reference
// tool's (simuprint.vercel.app) clean axis labels.
const niceMax = ( value ) => {
	if ( value <= 0 ) {
		return 1;
	}

	const magnitude = 10 ** Math.floor( Math.log10( value ) );
	const steps = [ 1, 2, 2.5, 5, 10 ];
	const step = steps.find( ( candidate ) => value <= candidate * magnitude );

	return ( step ?? 10 ) * magnitude;
};

// A rect path rounded on its top two corners only, bottom corners square —
// unlike SVG's <rect rx>, which rounds all four corners uniformly and, on a
// short bar (a small value next to a much larger one), makes the whole bar
// read as a rounded pill/blob rather than a bar. Caps the radius to the
// bar's own height so a very short bar still renders as a small rectangle
// with a subtle curve rather than two overlapping/self-intersecting arcs.
const roundedTopBarPath = ( x, y, width, height, radius ) => {
	const r = Math.max( 0, Math.min( radius, width / 2, height ) );

	if ( r === 0 ) {
		return `M ${ x } ${ y } H ${ x + width } V ${ y + height } H ${ x } Z`;
	}

	return `M ${ x } ${ y + r }
		A ${ r } ${ r } 0 0 1 ${ x + r } ${ y }
		H ${ x + width - r }
		A ${ r } ${ r } 0 0 1 ${ x + width } ${ y + r }
		V ${ y + height }
		H ${ x }
		Z`;
};

// Vertical column comparison — mirrors the reference tool's "Costo annuale
// — laboratorio vs interno" chart: y-axis gridlines with full (non-compact)
// currency labels, one category of side-by-side bars, a legend, a value
// chip per bar, and (matching the reference's own Recharts tooltip) a
// hover/focus tooltip listing every bar's value. Plain SVG rather than a
// charting library: there are only ever two or three bars, so a chart
// library is overkill.
//
// Fixed viewBox aspect ratio — CumulativeChart.js's own viewBox is sized
// to match this one exactly (both charts render at `width: 100%` in
// equal-width grid columns, so mismatched ratios alone would render them
// at different heights) — see CumulativeChart.js for why neither chart
// dynamically stretches to fill its card's height instead.
const BarChart = ( { bars, categoryLabel, currency, locale } ) => {
	const [ hoveredIndex, setHoveredIndex ] = useState( null );

	const maxValue = niceMax(
		Math.max( ...bars.map( ( bar ) => bar.value ), 1 )
	);
	const plotHeight = HEIGHT - PADDING_TOP - PADDING_BOTTOM;
	const plotWidth = WIDTH - PADDING_LEFT - PADDING_RIGHT;

	const barsWidth = bars.length * BAR_WIDTH + ( bars.length - 1 ) * BAR_GAP;
	const startX = PADDING_LEFT + ( plotWidth - barsWidth ) / 2;

	const barHeight = ( value ) => ( value / maxValue ) * plotHeight;
	const barY = ( value ) => HEIGHT - PADDING_BOTTOM - barHeight( value );
	const tickY = ( value ) => HEIGHT - PADDING_BOTTOM - barHeight( value );
	const barX = ( index ) => startX + index * ( BAR_WIDTH + BAR_GAP );

	const yTicks = Array.from(
		{ length: Y_TICK_COUNT + 1 },
		( _, index ) => ( maxValue * index ) / Y_TICK_COUNT
	);

	const hovered = hoveredIndex !== null ? bars[ hoveredIndex ] : null;
	const tooltipLeft = hovered
		? ( ( barX( hoveredIndex ) + BAR_WIDTH / 2 ) / WIDTH ) * 100
		: 0;
	const tooltipTop = hovered ? ( barY( hovered.value ) / HEIGHT ) * 100 : 0;

	return (
		<div className="roi-calculator__bar-chart">
			<div className="roi-calculator__bar-chart-plot">
				<svg
					className="roi-calculator__bar-chart-svg"
					viewBox={ `0 0 ${ WIDTH } ${ HEIGHT }` }
					role="img"
					aria-label={ categoryLabel }
				>
					{ yTicks.map( ( tickValue ) => (
						<g key={ tickValue }>
							<line
								x1={ PADDING_LEFT }
								x2={ WIDTH - PADDING_RIGHT }
								y1={ tickY( tickValue ) }
								y2={ tickY( tickValue ) }
								className="roi-calculator__bar-chart-grid"
							/>
							<text
								x={ PADDING_LEFT - 8 }
								y={ tickY( tickValue ) }
								textAnchor="end"
								dominantBaseline="middle"
								className="roi-calculator__bar-chart-axis-label"
							>
								{ formatCurrency(
									tickValue,
									currency,
									locale
								) }
							</text>
						</g>
					) ) }

					{ bars.map( ( bar, index ) => (
						<path
							key={ bar.label }
							d={ roundedTopBarPath(
								barX( index ),
								barY( bar.value ),
								BAR_WIDTH,
								Math.max( barHeight( bar.value ), 1 ),
								BAR_RADIUS
							) }
							tabIndex={ 0 }
							role="button"
							aria-label={ `${ bar.label }: ${ formatCurrency(
								bar.value,
								currency,
								locale
							) }` }
							className={
								'roi-calculator__bar-chart-bar' +
								( bar.variant
									? ` roi-calculator__bar-chart-bar--${ bar.variant }`
									: '' )
							}
							onMouseEnter={ () => setHoveredIndex( index ) }
							onMouseLeave={ () => setHoveredIndex( null ) }
							onFocus={ () => setHoveredIndex( index ) }
							onBlur={ () => setHoveredIndex( null ) }
						/>
					) ) }

					{ categoryLabel && (
						<text
							x={ PADDING_LEFT + plotWidth / 2 }
							y={ HEIGHT - PADDING_BOTTOM + 20 }
							textAnchor="middle"
							className="roi-calculator__bar-chart-axis-label"
						>
							{ categoryLabel }
						</text>
					) }
				</svg>

				{ hovered && (
					<div
						className="roi-calculator__bar-chart-tooltip"
						style={ {
							left: `${ tooltipLeft }%`,
							top: `${ tooltipTop }%`,
						} }
					>
						{ categoryLabel && (
							<strong className="roi-calculator__bar-chart-tooltip-title">
								{ categoryLabel }
							</strong>
						) }
						{ bars.map( ( bar, index ) => (
							<div
								key={ bar.label }
								className={
									'roi-calculator__bar-chart-tooltip-row' +
									( index === hoveredIndex
										? ' roi-calculator__bar-chart-tooltip-row--active' +
										  ( bar.variant
												? ` roi-calculator__bar-chart-tooltip-row--${ bar.variant }`
												: '' )
										: '' )
								}
							>
								{ bar.label } :{ ' ' }
								{ formatCurrency(
									bar.value,
									currency,
									locale
								) }
							</div>
						) ) }
					</div>
				) }
			</div>

			<div className="roi-calculator__bar-chart-legend">
				{ bars.map( ( bar ) => (
					<span
						className="roi-calculator__bar-chart-legend-item"
						key={ bar.label }
					>
						<span
							className={
								'roi-calculator__bar-chart-legend-dot' +
								( bar.variant
									? ` roi-calculator__bar-chart-legend-dot--${ bar.variant }`
									: '' )
							}
						/>
						{ bar.label }
					</span>
				) ) }
			</div>

			<div className="roi-calculator__bar-chart-chips">
				{ bars.map( ( bar ) => (
					<div
						className="roi-calculator__bar-chart-chip"
						key={ bar.label }
					>
						<span className="roi-calculator__bar-chart-chip-label">
							{ bar.label }
						</span>
						<span className="roi-calculator__bar-chart-chip-value">
							{ formatCurrency( bar.value, currency, locale ) }
						</span>
					</div>
				) ) }
			</div>
		</div>
	);
};

BarChart.propTypes = {
	bars: PropTypes.arrayOf(
		PropTypes.shape( {
			label: PropTypes.string.isRequired,
			value: PropTypes.number.isRequired,
			variant: PropTypes.string,
		} )
	).isRequired,
	categoryLabel: PropTypes.string,
	currency: PropTypes.string,
	locale: PropTypes.string,
};

export default BarChart;
